import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { aggregateCashierRows, CASHIER_DETAIL_LIMIT, CASHIER_EXPORT_LIMIT } from '@/lib/reporting/cashierEngine';
import { applyCashierDay } from '@/lib/reporting/cashierDay';
import { loadCashierDay } from '@/lib/reporting/cashierDayLoad';
import { parseCashierReportPayload } from '@/lib/reporting/cashierParse';
import { isOutstandingInvoice } from '@/lib/outstandingInvoice';
import type { CashierFilterState, CashierOutstandingInvoice, CashierReportResult } from '@/lib/reporting/cashierTypes';
import { CLINIC_TIMEZONE, resolveReportPeriod, toIsoRange } from '@/lib/reporting/periods';

function rpc<T>(name: string, args: Record<string, unknown>) {
  return supabase.rpc(name as never, args as never) as unknown as Promise<{
    data: T | null;
    error: Error | null;
  }>;
}

export function cashierReportQueryKey(
  clinicId: string | null,
  range: { start: string; end: string },
  filters: CashierFilterState,
  detailLimit: number,
) {
  return ['cashier-report', clinicId, range, filters, detailLimit] as const;
}

function filtersToRpc(filters: CashierFilterState) {
  return {
    practitioner_ids: filters.practitionerIds,
    location_ids: filters.locationIds,
    payment_methods: filters.paymentMethods,
    channels: filters.channels,
    statuses: filters.statuses,
    search: filters.search.trim(),
  };
}

async function fetchCashierReport(
  clinicId: string,
  filters: CashierFilterState,
  detailLimit: number,
): Promise<CashierReportResult> {
  const period = resolveReportPeriod(filters.period, filters.customFrom, filters.customTo);
  const range = toIsoRange(period);
  const { data, error } = await rpc<Record<string, unknown>>('list_cashier_report', {
    p_clinic_id: clinicId,
    p_starts_at: range.start,
    p_ends_at: range.end,
    p_filters: filtersToRpc(filters),
    p_tz: CLINIC_TIMEZONE,
    p_detail_limit: detailLimit,
  });
  if (error) throw error;
  if (!data) {
    return aggregateCashierRows([], period, { clinicName: null });
  }
  // #485 filters the till's unpaid list. The day reconciliation then replaces
  // outstanding with the same rule applied only to this period's services, so
  // behandlet − krediteret still equals betalt − refunderet + klip + udestående.
  const limited = await limitOutstandingToPeriod(parseCashierReportPayload(data, period), period);
  try {
    const day = await loadCashierDay(clinicId, range, filters, limited.transactions);
    return applyCashierDay(limited, day);
  } catch (dayError) {
    console.error('cashier day details failed', dayError);
    return limited;
  }
}

/** Kasse outstanding is the shared unpaid rule, limited to invoices issued in the period. */
async function limitOutstandingToPeriod(
  result: CashierReportResult,
  period: { start: Date; end: Date },
): Promise<CashierReportResult> {
  const ids = result.outstandingInvoices.map((invoice) => invoice.id);
  if (ids.length === 0) {
    return { ...result, summary: { ...result.summary, outstanding_cents: 0, outstanding_count: 0 } };
  }
  const { data, error } = await supabase
    .from('invoices')
    .select('id, kind, status, total_cents, total_paid_cents, issued_at')
    .in('id', ids);
  if (error) throw error;
  const byId = new Map((data ?? []).map((row) => [row.id, row]));
  const outstandingInvoices: CashierOutstandingInvoice[] = [];
  for (const invoice of result.outstandingInvoices) {
    const row = byId.get(invoice.id);
    if (!row?.issued_at) continue;
    const issued = new Date(row.issued_at);
    if (issued < period.start || issued > period.end) continue;
    if (
      !isOutstandingInvoice({
        kind: row.kind,
        status: row.status,
        totalCents: row.total_cents,
        paidCents: row.total_paid_cents,
      })
    ) {
      continue;
    }
    outstandingInvoices.push(invoice);
  }
  const outstandingCents = outstandingInvoices.reduce((sum, invoice) => sum + invoice.outstanding_cents, 0);
  return {
    ...result,
    outstandingInvoices,
    summary: {
      ...result.summary,
      outstanding_cents: outstandingCents,
      outstanding_count: outstandingInvoices.length,
    },
  };
}

export function useCashierReport(
  clinicId: string | null | undefined,
  filters: CashierFilterState,
  enabled: boolean,
  detailLimit = CASHIER_DETAIL_LIMIT,
) {
  const range = toIsoRange(
    resolveReportPeriod(filters.period, filters.customFrom, filters.customTo),
  );
  return useQuery({
    queryKey: cashierReportQueryKey(clinicId ?? null, range, filters, detailLimit),
    enabled: Boolean(clinicId) && enabled,
    queryFn: async (): Promise<CashierReportResult> => {
      if (!clinicId) {
        return aggregateCashierRows([], resolveReportPeriod(filters.period, filters.customFrom, filters.customTo));
      }
      return fetchCashierReport(clinicId, filters, detailLimit);
    },
    staleTime: 15_000,
  });
}

export function useCashierReportExport(
  clinicId: string | null | undefined,
  filters: CashierFilterState,
) {
  return {
    fetch: async () => {
      if (!clinicId) throw new Error('No active clinic');
      return fetchCashierReport(clinicId, filters, CASHIER_EXPORT_LIMIT);
    },
  };
}

export { parseCashierReportPayload };
